export type ResponseDateValue = Date | string | number | null | undefined;

const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
};

const dateFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit'
};

const timeFormatOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
};

const conversationDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
};

export const getBrowserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const parseResponseDate = (value: ResponseDateValue): Date | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value;
    }

    const normalizedValue = typeof value === 'string' ? normalizeApiDateString(value) : value;
    const date = new Date(normalizedValue);

    return Number.isNaN(date.getTime()) ? undefined : date;
}

export const getDateTimestamp = (value: ResponseDateValue): number => parseResponseDate(value)?.getTime() ?? 0;

export const getStandardDateFormat = (startTime: ResponseDateValue): string => {
    return formatBrowserDateTime(startTime);
}

export const formatBrowserDateTime = (value: ResponseDateValue): string => {
    return formatWithBrowserTimeZone(value, dateTimeFormatOptions);
}

export const formatBrowserDate = (value: ResponseDateValue): string => {
    return formatWithBrowserTimeZone(value, dateFormatOptions);
}

export const formatBrowserTime = (value: ResponseDateValue): string => {
    return formatWithBrowserTimeZone(value, timeFormatOptions);
}

export const formatBrowserConversationTime = (value: ResponseDateValue): string => {
    return formatWithBrowserTimeZone(value, conversationDateTimeFormatOptions);
}

const formatWithBrowserTimeZone = (value: ResponseDateValue, options: Intl.DateTimeFormatOptions): string => {
    const date = parseResponseDate(value);

    if (!date) {
        return '';
    }

    return new Intl.DateTimeFormat('en-US', {
        ...options,
        timeZone: getBrowserTimeZone()
    }).format(date);
}

const normalizeApiDateString = (value: string): string => {
    const trimmedValue = value.trim();
    const hasTimeComponent = /[tT ]\d{1,2}:\d{2}/.test(trimmedValue);
    const hasExplicitTimeZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(trimmedValue);

    if (hasTimeComponent && !hasExplicitTimeZone) {
        return `${trimmedValue}Z`;
    }

    return trimmedValue;
}
